// apps/client/src/hooks/useLineOfTrailKpi.ts
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).
import { useMemo } from "react";
import { useDashboardQuery } from "./useDashboardQuery";

interface UseLineOfTrailKpiParams {
  companyId: string;
  officeId?: string;
  templateId?: string;
  // Option A: Year-based (existing, backward compatible)
  year?: number;
  yearType?: 'calendar' | 'financial';
  // Option B: Month-range based (new - for relative periods)
  startMonth?: string;  // YYYY-MM format
  endMonth?: string;    // YYYY-MM format
  includeByBroker?: boolean;  // Return broker breakdown for activity status filtering
  includeByLender?: boolean;  // Return lender breakdown for lender filtering
  entityCode?: string;
}

// Broker LOT data for activity filtering
interface BrokerLotData {
  lot: number;
  loanAmount: number;
}

// Lender LOT data for lender filtering
interface LenderLotData {
  lot: number;
  loanAmount: number;
}

interface MonthlyLineOfTrailData {
  month: string;
  yearMonth?: string;  // 'YYYY-MM' for tooltip to display full month + year
  lot: number;
  loanAmount: number;
  byBroker?: Record<string, BrokerLotData>;  // Broker breakdown for activity filtering
  byLender?: Record<string, LenderLotData>;  // Lender breakdown for lender filtering
  [key: string]: string | number | Record<string, BrokerLotData> | Record<string, LenderLotData> | undefined;  // Index signature for chart compatibility
}

interface UseLineOfTrailKpiReturn {
  series: MonthlyLineOfTrailData[];
  loading: boolean;
  error: string | null;
}

// Cloud Function response type (matches getLineOfTrailGrowth)
interface LineOfTrailGrowthResponse {
  series: Array<{
    month: string;      // 'YYYY-MM' format from backend
    lot: number;        // COUNT of active loans
    loanAmount: number; // Total loan amount in cents
    byBroker?: Record<string, { lot: number; loanAmount: number }>;
    byLender?: Record<string, { lot: number; loanAmount: number }>;
  }>;
}

/**
 * Hook to fetch historical Line of Trail data (12-month trend)
 * MIGRATED: Now uses BigQuery via getLineOfTrailGrowth Cloud Function
 *
 * Migration Date: 2025-11-16
 * Previous: Queried Firestore commission_data collection
 * Current: Queries BigQuery raw_commissions table via Cloud Function
 */
export const useLineOfTrailKpi = ({
  companyId,
  officeId,
  templateId,
  year,
  yearType,
  startMonth,
  endMonth,
  includeByBroker,
  includeByLender,
  entityCode,
}: UseLineOfTrailKpiParams): UseLineOfTrailKpiReturn => {
  const { data, loading, error } = useDashboardQuery<LineOfTrailGrowthResponse>(
    'getLineOfTrailGrowth',
    {
      companyId,
      officeId,
      templateId,
      ...(startMonth && endMonth
        ? { startMonth, endMonth }
        : { year, yearType }),
      includeByBroker,
      includeByLender,
      entityCode,
    },
    { enabled: Boolean(year || (startMonth && endMonth)) }
  );

  // Transform backend response to frontend format
  // Backend returns month as 'YYYY-MM' (e.g., '2024-07')
  // Frontend needs short month label (e.g., 'Jul')
  const series = useMemo<MonthlyLineOfTrailData[]>(() => {
    if (!data) return [];

    return data.series.map((item) => {
      // Parse month from 'YYYY-MM' format
      const [yearStr, monthStr] = item.month.split('-');
      const monthIndex = parseInt(monthStr, 10) - 1; // 0-indexed
      const date = new Date(parseInt(yearStr, 10), monthIndex, 1);
      const monthLabel = date.toLocaleString('default', { month: 'short' });

      return {
        month: monthLabel,      // 'Jul', 'Aug', etc.
        yearMonth: item.month,  // 'YYYY-MM' for tooltip to display full month + year
        lot: item.lot,          // COUNT from BigQuery
        loanAmount: item.loanAmount, // CENTS from BigQuery
        ...(item.byBroker && { byBroker: item.byBroker }),  // Include broker breakdown if present
        ...(item.byLender && { byLender: item.byLender }),  // Include lender breakdown if present
      };
    });
  }, [data]);

  return { series, loading, error };
};
